import { measure } from '@/components/Analytics/Analytics';
import AchievementIcon from '@/components/Icon/AchievementIcon';
import CalendarIcon from '@/components/Icon/CalendarIcon';
import CollectionsIcon from '@/components/Icon/CollectionsIcon';
import useHaptics from '@/hooks/useHaptics';
import cn from '@/lib/cs';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { tabValues, useActiveTab } from './useActiveTab';
import { useUnlockedTabs } from './useUnlockedTabs';

const Tabs = ({ isVisible }: { isVisible: boolean }) => {
	// Get the active tab from the tab URL parameter
	const activeTabValue = useActiveTab();

	return (
		<nav className="fixed inset-x-0 bottom-0 z-40 w-full landscape:relative landscape:h-full landscape:w-auto">
			<div className="relative landscape:h-full landscape:min-h-0 landscape:overflow-y-auto landscape:overflow-x-hidden landscape:bg-accent xl:landscape:px-8">
				<TabsBackground />
				<ul
					aria-label="Navigatie"
					className="relative flex items-center justify-center gap-4 xs:gap-8 lg:gap-16 landscape:h-full landscape:min-h-0 landscape:flex-col landscape:gap-8">
					<Tab
						value="calendar"
						isActive={isVisible && activeTabValue === 'calendar'}
						displayName="Kalender"
						icon={CalendarIcon}
					/>
					<Tab
						value="collections"
						isActive={isVisible && activeTabValue === 'collections'}
						displayName="Kwartetten"
						icon={CollectionsIcon}
					/>
					<Tab
						value="achievements"
						isActive={
							isVisible && activeTabValue === 'achievements'
						}
						displayName="Challenges"
						icon={AchievementIcon}
					/>
				</ul>
			</div>
		</nav>
	);
};

const Tab = ({
	value,
	isActive,
	displayName,
	icon,
}: {
	value: (typeof tabValues)[number];
	isActive: boolean;
	displayName: string;
	icon: React.FC<React.SVGProps<SVGSVGElement>>;
}) => {
	const unlockedTabs = useUnlockedTabs();
	const { withOnButtonPress } = useHaptics();

	const IconComponent = icon;

	const [searchParams, setSearchParams] = useSearchParams();

	const isRendered = useMemo(
		() => unlockedTabs.includes(value),
		[unlockedTabs, value],
	);

	if (!isRendered) return null;

	return (
		<li
			className={cn(
				'relative text-base w-28 landscape:max-xxs:w-16 min-h-0',
				unlockedTabs.length > 2 && 'portrait:max-xxs:-mx-2',
				isActive ? 'text-primary' : 'text-accentCompat',
			)}>
			<ActiveTabBackground isActive={isActive} />
			<motion.a
				className="relative flex flex-col items-center justify-center gap-1 rounded-lg p-1 pt-2 landscape:max-xxs:p-0"
				aria-label={displayName}
				whileTap={{
					scale: 0.9,
				}}
				href={`?tab=${value}`}
				onClick={(e) => {
					e.preventDefault();
					if (isActive) return;
					measure(`tab-${value}`, 'navigation');
					setSearchParams(
						{
							...Object.fromEntries(searchParams.entries()),
							tab: value,
						},
						{
							replace: true,
						},
					);
				}}
				{...withOnButtonPress}>
				<IconComponent
					className={cn(
						'max-xxs:h-10 h-12 lg:h-12 transition-transform',
						isActive ? 'scale-100' : 'scale-95',
					)}
					viewBox="0 0 24 24"
					aria-hidden
				/>
				<span className="transition-all landscape:hidden landscape:xxs:block">
					{displayName}
				</span>
			</motion.a>
		</li>
	);
};

const TabsBackground = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		className="absolute inset-0 size-full text-accent"
		viewBox="0 0 360 80"
		preserveAspectRatio="none">
		<path fill="currentColor" d="M0 2s113 5 180 0 180 3 180 3v75H0V2z" />
	</svg>
);

const ActiveTabBackground = ({ isActive }: { isActive: boolean }) => (
	<div
		className={twMerge(
			'absolute inset-0 size-full transition-all delay-75 ease-out',
			isActive
				? 'opacity-100 scale-100'
				: 'opacity-0 scale-50 translate-y-1/2',
		)}>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			className="overflow-visible text-gray-50 xxs:scale-110"
			viewBox="0 0 112.7 104.07">
			<path
				fill="currentColor"
				d="M42 102c-4-1-21-5-32-20-2-3-9-13-9-28s8-26 12-31c5-7 10-11 14-13l10-6 16-3c11-2 25 0 35 4l9 5s4 4 6 9c19 37 6 60 6 60-13 24-46 28-67 23z"
			/>
		</svg>
	</div>
);

export default Tabs;
